import React, { useEffect, useState } from "react";
import { api, galleryImageUrl } from "@/lib/api";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Edit, Upload, DollarSign, Users, CheckCircle2, Clock, ImagePlus, Star, Eye, EyeOff, FileDown, Navigation, MessageCircle, Truck, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { API } from "@/lib/api";
import { AvailabilityManager } from "@/components/admin/AvailabilityManager";
import { InstagramManager } from "@/components/admin/InstagramManager";

const statusOpts = [
  { v: "pending", l: "Aguardando" },
  { v: "approved", l: "Aprovado" },
  { v: "in_progress", l: "Em andamento" },
  { v: "completed", l: "Concluído" },
  { v: "canceled", l: "Cancelado" },
  { v: "diagnosis_only", l: "Diag. não aprovado" },
];

const statusCls = {
  pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  in_progress: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  completed: "bg-green-500/15 text-green-300 border-green-500/30",
  canceled: "bg-red-500/15 text-red-300 border-red-500/30",
  diagnosis_only: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};

export default function Admin() {
  const now = new Date();
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [stats, setStats] = useState({ total_customers: 0, total_bookings: 0, pending_bookings: 0, completed_bookings: 0, revenue: 0 });
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [fees, setFees] = useState({ travel_fee: 0, diagnosis_fee: 0, pix_key: "", whatsapp: "", instagram: "" });

  const [editSvc, setEditSvc] = useState(null);
  const [svcForm, setSvcForm] = useState({ name: "", description: "", price: 0, duration_minutes: 60, image_url: "" });
  const [svcOpen, setSvcOpen] = useState(false);

  const [galOpen, setGalOpen] = useState(false);
  const [galForm, setGalForm] = useState({ title: "", description: "", vehicle: "" });
  const [galFile, setGalFile] = useState(null);

  const refresh = async () => {
    const [s, b, sv, g, f, rv] = await Promise.all([
      api.get("/admin/stats"), api.get("/bookings"), api.get("/services"), api.get("/gallery"), api.get("/config/fees"), api.get("/reviews"),
    ]);
    setStats(s.data); setBookings(b.data); setServices(sv.data); setGallery(g.data); setFees(f.data); setReviews(rv.data);
  };

  useEffect(() => { refresh().catch(() => {}); }, []);

  const updateFees = async () => {
    try {
      await api.put("/config/fees", {
        travel_fee: Number(fees.travel_fee) || 0,
        diagnosis_fee: Number(fees.diagnosis_fee) || 0,
        pix_key: fees.pix_key || "",
        whatsapp: fees.whatsapp || "",
        instagram: (fees.instagram || "").replace(/^@/, ""),
      });
      toast.success("Taxas atualizadas");
    } catch { toast.error("Erro ao atualizar"); }
  };

  const changeStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status`, { status }); toast.success("Status atualizado"); await refresh(); }
    catch { toast.error("Erro ao atualizar"); }
  };
  const changePayment = async (id, s) => {
    try { await api.put(`/bookings/${id}/payment`, { payment_status: s }); toast.success("Pagamento"); await refresh(); }
    catch { toast.error("Erro"); }
  };

  const updateTracking = async (id, tech_status, eta_minutes = null) => {
    try {
      await api.put(`/bookings/${id}/tracking`, { tech_status, eta_minutes });
      toast.success("Rastreio atualizado");
      await refresh();
    } catch { toast.error("Erro"); }
  };

  const sendWhatsAppReminder = (b) => {
    const phone = (b.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error("Cliente sem telefone"); return; }
    const normalized = phone.startsWith("55") ? phone : `55${phone}`;
    const dateStr = new Date(b.scheduled_date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    const msg = encodeURIComponent(
      `Olá, ${b.customer_name}! Passando para lembrar do seu atendimento TSBRASIL Ar-Condicionado em ${dateStr}. ` +
      `Veículo: ${b.car_brand} ${b.car_model}. Endereço: ${b.address}. Qualquer dúvida, estamos aqui!`
    );
    window.open(`https://wa.me/${normalized}?text=${msg}`, "_blank");
  };

  const saveService = async () => {
    try {
      const payload = { ...svcForm, price: Number(svcForm.price), duration_minutes: Number(svcForm.duration_minutes) };
      if (editSvc) await api.put(`/services/${editSvc}`, payload);
      else await api.post("/services", payload);
      toast.success("Serviço salvo");
      setSvcOpen(false); setEditSvc(null); setSvcForm({ name: "", description: "", price: 0, duration_minutes: 60, image_url: "" });
      await refresh();
    } catch { toast.error("Erro"); }
  };

  const delService = async (id) => {
    if (!window.confirm("Excluir serviço?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Removido"); await refresh(); } catch { toast.error("Erro"); }
  };

  const uploadGal = async () => {
    if (!galFile) { toast.error("Escolha uma imagem"); return; }
    try {
      const fd = new FormData(); fd.append("file", galFile);
      await api.post(`/gallery/upload?title=${encodeURIComponent(galForm.title || "Trabalho")}&description=${encodeURIComponent(galForm.description || "")}&vehicle=${encodeURIComponent(galForm.vehicle || "")}`, fd);
      toast.success("Foto enviada");
      setGalOpen(false); setGalForm({ title: "", description: "", vehicle: "" }); setGalFile(null);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no upload"); }
  };

  const delGal = async (id) => {
    if (!window.confirm("Excluir foto?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Removida"); await refresh(); } catch { toast.error("Erro"); }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("ra_token");
      const url = `${API}/admin/report/monthly?year=${reportYear}&month=${reportMonth}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      if (!res.ok) throw new Error("Erro ao gerar");
      const blob = await res.blob();
      const link = document.createElement("a");
      const href = URL.createObjectURL(blob);
      link.href = href;
      link.download = `tsbrasil-relatorio-${reportYear}-${String(reportMonth).padStart(2,"0")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      toast.success("Relatório gerado");
    } catch { toast.error("Erro ao gerar relatório"); }
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="p-5">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
        <div className="text-2xl font-black text-white font-mono">{value}</div>
        <div className="text-xs text-slate-400 uppercase tracking-wider font-mono mt-1">{label}</div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-2">Painel administrativo</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Gestão TSBRASIL Ar-Condicionado</h1>
          </div>
          <div className="flex items-end gap-2 bg-slate-900/50 border border-slate-800 rounded-lg p-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-sky-400 mb-1">Relatório PDF</div>
              <div className="flex gap-1.5">
                <Select value={String(reportMonth)} onValueChange={(v) => setReportMonth(Number(v))}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white w-32 h-9" data-testid="report-month"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"].map((m, i) => (
                      <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" value={reportYear} onChange={(e) => setReportYear(Number(e.target.value))} className="bg-slate-800 border-slate-700 text-white w-24 h-9" data-testid="report-year" />
              </div>
            </div>
            <Button onClick={downloadReport} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold h-9" data-testid="download-report-btn">
              <FileDown className="w-4 h-4 mr-1" /> Baixar
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          <StatCard icon={Users} label="Clientes" value={stats.total_customers} color="bg-sky-500/15 text-sky-400" />
          <StatCard icon={Clock} label="Pendentes" value={stats.pending_bookings} color="bg-yellow-500/15 text-yellow-400" />
          <StatCard icon={CheckCircle2} label="Concluídos" value={stats.completed_bookings} color="bg-emerald-500/15 text-emerald-400" />
          <StatCard icon={DollarSign} label="Faturamento" value={`R$ ${Number(stats.revenue).toFixed(0)}`} color="bg-cyan-500/15 text-cyan-400" />
          <StatCard icon={CheckCircle2} label="Total" value={stats.total_bookings} color="bg-blue-500/15 text-blue-400" />
        </div>

        <Tabs defaultValue="bookings" className="w-full">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Agendamentos</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Serviços</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Galeria</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Avaliações</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Agenda</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="tab-instagram">Instagram</TabsTrigger>
            <TabsTrigger value="fees" data-testid="tab-fees">Taxas</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="grid gap-3">
              {bookings.map((b) => (
                <Card key={b.booking_id} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={statusCls[b.status] + " font-mono text-xs uppercase"}>{statusOpts.find((o) => o.v === b.status)?.l}</Badge>
                          <Badge className="bg-slate-800 text-slate-300 border-slate-700 font-mono text-xs">
                            {b.payment_status === "paid" ? "💰 Pago" : "⏳ Pgto pendente"}
                          </Badge>
                          <span className="text-xs text-slate-500 font-mono">#{b.booking_id.slice(-6)}</span>
                        </div>
                        <h4 className="text-white font-bold">{b.customer_name} • {b.phone}</h4>
                        <p className="text-sm text-slate-400 mt-0.5">{b.car_brand} {b.car_model} {b.car_year} {b.plate && `• ${b.plate}`}</p>
                        <p className="text-sm text-slate-300 mt-2">📍 {b.address}</p>
                        <p className="text-sm text-slate-300 mt-1">🔧 {b.problem_description}</p>
                        <p className="text-xs text-sky-400 font-mono mt-2">{format(new Date(b.scheduled_date), "PPp", { locale: ptBR })}</p>
                      </div>
                      <div className="flex flex-col gap-2 lg:w-56">
                        <div className="text-right lg:text-left">
                          <div className="text-xs text-slate-500 font-mono uppercase">Total</div>
                          <div className="text-2xl font-black text-sky-400 font-mono">R$ {Number(b.total).toFixed(2)}</div>
                        </div>
                        <Select value={b.status} onValueChange={(v) => changeStatus(b.booking_id, v)}>
                          <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid={`admin-status-${b.booking_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            {statusOpts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={() => changePayment(b.booking_id, b.payment_status === "paid" ? "pending" : "paid")} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`admin-pay-${b.booking_id}`}>
                          {b.payment_status === "paid" ? "Marcar como pendente" : "Marcar como pago"}
                        </Button>
                        <div className="pt-2 border-t border-slate-800">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-sky-400 mb-1.5">Rastreio</div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <Button size="sm" variant={b.tech_status === "on_the_way" ? "default" : "outline"} onClick={() => {
                              const eta = window.prompt("ETA em minutos:", b.eta_minutes || "20");
                              if (eta !== null) updateTracking(b.booking_id, "on_the_way", Number(eta));
                            }} className={b.tech_status === "on_the_way" ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "border-slate-700 text-slate-200 hover:bg-slate-800"} data-testid={`track-way-${b.booking_id}`}>
                              <Navigation className="w-3.5 h-3.5 mr-1" /> A caminho
                            </Button>
                            <Button size="sm" variant={b.tech_status === "arrived" ? "default" : "outline"} onClick={() => updateTracking(b.booking_id, "arrived")} className={b.tech_status === "arrived" ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "border-slate-700 text-slate-200 hover:bg-slate-800"} data-testid={`track-arrived-${b.booking_id}`}>
                              <Truck className="w-3.5 h-3.5 mr-1" /> Cheguei
                            </Button>
                            <Button size="sm" variant={b.tech_status === "working" ? "default" : "outline"} onClick={() => updateTracking(b.booking_id, "working")} className={b.tech_status === "working" ? "bg-cyan-500 text-slate-950 hover:bg-cyan-400" : "border-slate-700 text-slate-200 hover:bg-slate-800"} data-testid={`track-working-${b.booking_id}`}>
                              <Wrench className="w-3.5 h-3.5 mr-1" /> Trabalhando
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => updateTracking(b.booking_id, "idle")} className="border-slate-700 text-slate-400 hover:bg-slate-800">
                              <X className="w-3.5 h-3.5 mr-1" /> Limpar
                            </Button>
                          </div>
                          {b.tech_status === "on_the_way" && b.eta_minutes != null && (
                            <div className="text-xs text-sky-400 font-mono mt-1">ETA: {b.eta_minutes} min</div>
                          )}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => sendWhatsAppReminder(b)} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 w-full" data-testid={`wa-reminder-${b.booking_id}`}>
                          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Lembrete WhatsApp
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {bookings.length === 0 && <div className="text-slate-400 text-center py-10">Nenhum agendamento ainda.</div>}
            </div>
          </TabsContent>

          <TabsContent value="services" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={svcOpen} onOpenChange={(o) => { setSvcOpen(o); if (!o) { setEditSvc(null); setSvcForm({ name: "", description: "", price: 0, duration_minutes: 60, image_url: "" }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="admin-new-service"><Plus className="w-4 h-4 mr-1" /> Novo serviço</Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader><DialogTitle className="text-white">{editSvc ? "Editar" : "Novo"} serviço</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label className="text-slate-300">Nome</Label><Input value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <div><Label className="text-slate-300">Descrição</Label><Textarea value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-slate-300">Preço (R$)</Label><Input type="number" step="0.01" value={svcForm.price} onChange={(e) => setSvcForm({ ...svcForm, price: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                      <div><Label className="text-slate-300">Duração (min)</Label><Input type="number" value={svcForm.duration_minutes} onChange={(e) => setSvcForm({ ...svcForm, duration_minutes: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    </div>
                    <div><Label className="text-slate-300">URL da imagem (opcional)</Label><Input value={svcForm.image_url} onChange={(e) => setSvcForm({ ...svcForm, image_url: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <Button onClick={saveService} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="admin-save-service">Salvar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {services.map((s) => (
                <Card key={s.service_id} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-4 flex items-center gap-4">
                    {s.image_url && <img src={s.image_url} alt="" className="w-16 h-16 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-white truncate">{s.name}</h4>
                      <div className="text-sm text-sky-400 font-mono">R$ {Number(s.price).toFixed(2)} • {s.duration_minutes}min</div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => { setEditSvc(s.service_id); setSvcForm({ name: s.name, description: s.description, price: s.price, duration_minutes: s.duration_minutes, image_url: s.image_url || "" }); setSvcOpen(true); }} className="text-sky-400 hover:bg-slate-800" data-testid={`edit-svc-${s.service_id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delService(s.service_id)} className="text-red-400 hover:bg-red-500/10" data-testid={`del-svc-${s.service_id}`}><Trash2 className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="gallery" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={galOpen} onOpenChange={setGalOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="admin-new-gallery"><ImagePlus className="w-4 h-4 mr-1" /> Nova foto</Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader><DialogTitle className="text-white">Nova foto</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label className="text-slate-300">Título</Label><Input value={galForm.title} onChange={(e) => setGalForm({ ...galForm, title: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <div><Label className="text-slate-300">Veículo</Label><Input value={galForm.vehicle} onChange={(e) => setGalForm({ ...galForm, vehicle: e.target.value })} placeholder="Honda Civic 2020" className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <div><Label className="text-slate-300">Descrição</Label><Textarea value={galForm.description} onChange={(e) => setGalForm({ ...galForm, description: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1" /></div>
                    <div><Label className="text-slate-300">Imagem</Label><Input type="file" accept="image/*" onChange={(e) => setGalFile(e.target.files?.[0])} className="bg-slate-800 border-slate-700 text-white mt-1 file:text-sky-400" data-testid="gallery-file" /></div>
                    <Button onClick={uploadGal} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="admin-upload-gallery"><Upload className="w-4 h-4 mr-1" /> Enviar</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.map((g) => (
                <Card key={g.gallery_id} className="bg-slate-900/50 border-slate-800 overflow-hidden">
                  <img src={galleryImageUrl(g.gallery_id)} alt={g.title} className="w-full h-48 object-cover" />
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="min-w-0">
                      <h4 className="font-bold text-white truncate">{g.title}</h4>
                      {g.vehicle && <p className="text-xs text-sky-400 font-mono">{g.vehicle}</p>}
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => delGal(g.gallery_id)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            <div className="grid gap-3">
              {reviews.length === 0 && <div className="text-slate-400 text-center py-10">Nenhuma avaliação ainda.</div>}
              {reviews.map((r) => (
                <Card key={r.review_id} className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < r.rating ? "text-yellow-400 fill-yellow-400" : "text-slate-700"}`} />
                        ))}
                        <Badge className={r.published ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-slate-800 text-slate-400 border-slate-700"}>
                          {r.published ? "Publicada" : "Oculta"}
                        </Badge>
                      </div>
                      <p className="text-slate-200 text-sm">"{r.comment}"</p>
                      <p className="text-xs font-mono text-sky-400 mt-2">— {r.customer_name}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => { await api.put(`/reviews/${r.review_id}/publish`, { published: !r.published }); toast.success(!r.published ? "Publicada" : "Ocultada"); refresh(); }}
                        className="border-slate-700 text-slate-200 hover:bg-slate-800"
                        data-testid={`toggle-review-${r.review_id}`}
                      >
                        {r.published ? <><EyeOff className="w-4 h-4 mr-1" /> Ocultar</> : <><Eye className="w-4 h-4 mr-1" /> Publicar</>}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={async () => { if (!window.confirm("Excluir?")) return; await api.delete(`/reviews/${r.review_id}`); toast.success("Removida"); refresh(); }} className="text-red-400 hover:bg-red-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <AvailabilityManager />
          </TabsContent>

          <TabsContent value="instagram" className="mt-6">
            <InstagramManager />
          </TabsContent>

          <TabsContent value="fees" className="mt-6">
            <Card className="bg-slate-900/50 border-slate-800 max-w-xl">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-slate-300">Taxa de deslocamento (R$)</Label>
                  <Input type="number" step="0.01" value={fees.travel_fee} onChange={(e) => setFees({ ...fees, travel_fee: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="fees-travel" />
                  <p className="text-xs text-slate-500 mt-1">Somada ao valor do serviço aprovado.</p>
                </div>
                <div>
                  <Label className="text-slate-300">Taxa de diagnóstico (R$)</Label>
                  <Input type="number" step="0.01" value={fees.diagnosis_fee} onChange={(e) => setFees({ ...fees, diagnosis_fee: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="fees-diagnosis" />
                  <p className="text-xs text-slate-500 mt-1">Cobrada quando o orçamento não é aprovado.</p>
                </div>
                <div>
                  <Label className="text-slate-300">Chave PIX</Label>
                  <Input value={fees.pix_key || ""} onChange={(e) => setFees({ ...fees, pix_key: e.target.value })} className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-slate-300">WhatsApp</Label>
                  <Input value={fees.whatsapp || ""} onChange={(e) => setFees({ ...fees, whatsapp: e.target.value })} placeholder="5581998736850" className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-slate-300">Instagram (@usuário)</Label>
                  <Input value={fees.instagram || ""} onChange={(e) => setFees({ ...fees, instagram: e.target.value })} placeholder="rotatsbrasil" className="bg-slate-800 border-slate-700 text-white mt-1.5 h-11" data-testid="fees-instagram" />
                </div>
                <Button onClick={updateFees} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold h-11" data-testid="fees-save">Salvar configurações</Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </section>
      <Footer />
    </div>
  );
}
